// Upload Service v2 manager: one upload per event (keyed by slug) that keeps
// running when the Manage Video screen is closed. No React here; the hook in
// useUploadV2.js subscribes to it.
//
// Per-event state (getState / subscribe):
//   idle → uploading ⇄ paused → completing → queued → converting → done
//                        ↘ failed (errorStage "upload", canRetry when the same
//                          session can continue)  ↘ failed (errorStage "conversion";
//                          retryConversion() queues it again)
//   uploading → duplicate: evt.live already has this file from the last 24 h
//                          (uploadAgain() sends it anyway, cancel() drops it)
//   uploading / paused / failed (upload) → cancelled: cancel() (or Android's
//                          notification) stopped it; shown until dismiss()
// idle can carry `resumable`: an unfinished session remembered on this device
// (the user has to pick the same file again).

import { t } from "@/localization";
import {
  MAX_UPLOAD_BYTES,
  UPLOAD_INTERRUPTED_MESSAGE,
  UPLOAD_V2_DEFAULTS,
  UploadV2Error,
  abortError,
  createStatusWatcher,
  isAbortError,
  normalizeStatus,
  parseExpiresAt,
  planParts,
  runUpload,
  sleep as defaultSleep,
  uploadedPartNumbers,
} from "./core";
import { fingerprintFor } from "./resumeStore";

const ACTIVE = new Set([
  "uploading",
  "paused",
  "completing",
  "queued",
  "converting",
]);
const SENDING = new Set(["uploading", "completing"]);
const WATCHED = new Set(["queued", "converting"]);
const FINISHED_ON_SERVER = new Set(["queued", "converting", "done"]);
const GONE = new Set([401, 403, 404, 410]);
const MODES = new Set(["replace", "append", "prepend", "insert"]);

// English on purpose: never shown as such (UploadV2NoticeView compares
// evt.live's errorMessage with it and says what to do instead).
export const CONVERSION_FAILED_MESSAGE = "We couldn't process this video.";
const EMPTY_FILE_MESSAGE = t(
  "This file is empty. Please choose another video."
);
const TOO_LARGE_MESSAGE = t(
  "This file is too large. Videos can be up to 200 GB."
);
const BUSY_MESSAGE = t("Another upload for this event is still in progress.");
const NOTHING_TO_RESUME_MESSAGE = t("There's no upload to continue.");
const RETRY_FAILED_MESSAGE = t(
  "Could not start the conversion again. Please try again later."
);
const PICK_AGAIN_MESSAGE = t("Choose the file again to continue the upload.");
const differentFileMessage = (name) =>
  t("That's a different file. Choose “{name}” to continue this upload.", {
    name,
  });

const VIDEO_TYPES = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  qt: "video/quicktime",
  mkv: "video/x-matroska",
  webm: "video/webm",
  avi: "video/x-msvideo",
  mts: "video/mp2t",
  m2ts: "video/mp2t",
  "3gp": "video/3gpp",
};

export const videoContentType = ({ name, type } = {}) => {
  if (typeof type === "string" && type.trim()) return type.trim();
  const extension = String(name || "")
    .split(".")
    .pop()
    .toLowerCase();
  return VIDEO_TYPES[extension] || "application/octet-stream";
};

const normalizeMode = (mode) => (MODES.has(mode) ? mode : "replace");
const indexOrNull = (value) => (Number.isInteger(value) ? value : null);

const toTime = (value) => {
  if (value == null || value === "") return null;
  const time = typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

const progressOf = (value) => {
  const progress = Number(value);
  return Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : null;
};

const sameFile = (expected, actual) =>
  expected.name === actual.name &&
  expected.size === actual.size &&
  (expected.lastModified == null ||
    actual.lastModified == null ||
    expected.lastModified === actual.lastModified);

// An event object loaded from the API (not the { broadcaster, slug }
// placeholder a screen starts with).
const isLoadedEvent = (event) =>
  Boolean(
    event &&
      typeof event === "object" &&
      ("vod_converted" in event || "datetime_uploaded" in event)
  );

// evt.live sets vod_converted=false straight in DynamoDB (no eventUpdated),
// so an event that still says vod_converted: true may be the old video. A
// finished conversion also stamps a new datetime_uploaded.
export function eventShowsNewConversion(event, baselineUploadedAt) {
  if (!event || event.vod_converted !== true || !event.datetime_uploaded) {
    return false;
  }
  if (baselineUploadedAt == null || baselineUploadedAt === "") return true;
  const current = toTime(event.datetime_uploaded);
  const baseline = toTime(baselineUploadedAt);
  if (current != null && baseline != null) return current > baseline;
  return String(event.datetime_uploaded) !== String(baselineUploadedAt);
}

// The worker's stages while converting, in order (evt.live reports
// "processing" when the worker named none).
const STAGE_RANK = { queued: 0, downloading: 1, processing: 2, publishing: 3 };

const conversionStageOf = (state, stage) => {
  if (state === "queued") return "queued";
  return STAGE_RANK[stage] > 0 ? stage : "processing";
};

const medianOf = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

// A time left (seconds) that counts down with the clock and only takes a new
// estimate when it is clearly different (25% and 10 s, either way) on
// `confirm` updates in a row, so bursts and stalls don't make it bounce.
// memory: { shown, at, drift } or null; estimate: seconds or null (hold).
export function steadyEta(memory, estimate, nowMs, confirm = 2) {
  if (estimate == null || !Number.isFinite(estimate)) return memory;
  if (!memory || memory.shown == null) {
    return { shown: Math.max(0, estimate), at: nowMs, drift: null };
  }
  const expected = Math.max(0, memory.shown - (nowMs - memory.at) / 1000);
  const direction =
    estimate > expected * 1.25 + 10
      ? 1
      : estimate < expected / 1.25 - 10
      ? -1
      : 0;
  const drift = direction
    ? {
        direction,
        count:
          direction === memory.drift?.direction ? memory.drift.count + 1 : 1,
      }
    : null;
  if (drift && drift.count >= confirm) {
    return { shown: Math.max(0, estimate), at: nowMs, drift: null };
  }
  return { shown: expected, at: nowMs, drift };
}

const laterUploadedAt = (left, right) => {
  if (left == null) return right ?? null;
  if (right == null) return left;
  const leftTime = toTime(left);
  const rightTime = toTime(right);
  if (leftTime != null && rightTime != null) {
    return rightTime > leftTime ? right : left;
  }
  return right;
};

const BLANK_STATE = {
  status: "idle",
  detail: null,
  id: null,
  filename: null,
  size: 0,
  mode: null,
  insertIndex: null,
  notifyGuests: false,
  meta: null,
  bytesUploaded: 0,
  bytesSent: 0,
  percent: 0,
  conversionProgress: null,
  error: null,
  errorStage: null,
  canRetry: false,
  doneVia: null,
  resumable: null,
  hasFile: false,
  duplicate: null,
  retrying: false,
  retryError: null,
  resumeNotice: null,
  speed: null,
  etaSeconds: null,
  conversionStage: null,
  conversionNote: null,
  conversionEtaSeconds: null,
  conversionStalled: false,
  queuedLong: false,
  retryable: true,
};

export const idleUploadState = Object.freeze({ slug: null, ...BLANK_STATE });

// A session as listed by GET ?slug= (field names normalized).
const listedSession = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id || raw.upload_id;
  if (!id) return null;
  return {
    id,
    status: normalizeStatus(raw.status),
    error: raw.error ?? null,
    errorMessage: raw.errorMessage ?? raw.error_message ?? null,
    filename: raw.filename || raw.file_name || null,
    size: Number(raw.size) || 0,
    mode: raw.mode || null,
    insertIndex: indexOrNull(raw.insertIndex ?? raw.insert_index),
    stage: raw.stage ?? null,
    progress: raw.progress,
    progressUpdatedAt: raw.progressUpdatedAt ?? raw.progress_updated_at ?? null,
    retryable: raw.retryable,
    queuedAt: raw.queuedAt ?? raw.queued_at ?? null,
    updatedAt:
      raw.updatedAt ||
      raw.updated_at ||
      raw.createdAt ||
      raw.created_at ||
      null,
  };
};

export function createUploadManager({
  api,
  adapter,
  store = null,
  options = {},
  now = () => Date.now(),
  random = Math.random,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
  sleep,
  log = () => {},
} = {}) {
  const settings = {
    ...UPLOAD_V2_DEFAULTS,
    progressIntervalMs: 200,
    // Conversion: status every pollIntervalMs, every slowPollIntervalMs once
    // it has taken longer than slowPollAfterMs.
    slowPollAfterMs: 10 * 60 * 1000,
    slowPollIntervalMs: 30000,
    // Processing time left: median progress rate over this window, hidden
    // ("Still processing…") after this long without progress.
    progressWindowMs: 60000,
    progressStallMs: 90000,
    // Queued this long: "Processing starting soon — we're finishing other videos".
    queuedLongMs: 2 * 60 * 1000,
    // "Resuming — X of Y MB were already uploaded" stays this long.
    resumeNoticeMs: 10000,
    // Upload speed: an exponentially weighted average (time constant ~10 s)
    // of the rate measured every half second, skipping the first 2 s of each
    // run (the browser counts bytes into socket buffers then, which reads
    // too fast); what the UI sees changes at most every 2 s (and only after
    // 2 s of data).
    speedWarmupMs: 2000,
    speedTimeConstantMs: 10000,
    // The time left uses the median half-second rate of the last 30 s, so
    // bursts and stalls shorter than ~15 s (parts changing over) don't move it.
    etaWindowMs: 30000,
    speedSampleMs: 500,
    speedRefreshMs: 2000,
    // No bytes for this long: the speed and time left are hidden (they'd be
    // stale), and measured afresh once bytes move again.
    speedStaleMs: 5000,
    discoverIntervalMs: 15000,
    failureMaxAgeMs: 7 * 24 * 60 * 60 * 1000,
    ...(adapter?.defaults || {}),
    ...options,
  };
  const wait =
    sleep || ((ms, signal) => defaultSleep(ms, signal, setTimer, clearTimer));

  const jobs = new Map();
  const snapshots = new Map();
  const idleSnapshots = new Map();
  const listeners = new Map();
  const retainCounts = new Map();
  const discoveries = new Map();
  const lastDiscoveryAt = new Map();
  const lastEvents = new Map();
  let leaveGuard = false;

  const describe = (file) =>
    adapter?.describeFile
      ? adapter.describeFile(file)
      : {
          name: String(file?.name || "video"),
          size: Number(file?.size) || 0,
          lastModified: Number.isFinite(file?.lastModified)
            ? file.lastModified
            : null,
          type: typeof file?.type === "string" ? file.type : "",
        };

  const clientInfo = () => {
    try {
      return adapter?.clientInfo?.() || undefined;
    } catch (error) {
      return undefined;
    }
  };

  const baselineFrom = (event) =>
    isLoadedEvent(event)
      ? { uploadedAt: event.datetime_uploaded ?? null }
      : undefined;

  const newJob = (slug, fields) => ({
    slug,
    file: null,
    info: { name: "video", size: 0, lastModified: null, type: "" },
    fingerprint: null,
    mode: "replace",
    insertIndex: null,
    notifyGuests: false,
    meta: null,
    baseline: undefined,
    session: null,
    parts: null,
    done: new Set(),
    status: "idle",
    detail: null,
    ackedBytes: 0,
    inflightBytes: 0,
    conversionProgress: null,
    error: null,
    serverError: null,
    errorStage: null,
    canRetry: false,
    doneVia: null,
    resumable: null,
    resumeToken: null,
    force: false,
    duplicate: null,
    retrying: false,
    retryError: null,
    run: null,
    watcher: null,
    lastPublishAt: 0,
    resumeNotice: null,
    resumeNoticeTimer: null,
    conversionStage: null,
    conversionNote: null,
    stageSince: null,
    watchSince: null,
    progressSamples: [],
    progressChangedAt: null,
    conversionEta: null,
    retryable: true,
    etaMemory: null,
    lastSent: null,
    speedSince: null,
    measuringSince: null,
    speedTotal: { bytes: 0, ms: 0 },
    rateSamples: [],
    etaRate: null,
    sampleStartedAt: null,
    sampleBytes: 0,
    speedAverage: null,
    shownSpeed: null,
    shownEta: null,
    shownAt: null,
    lastMovedAt: null,
    stallTimer: null,
    // "cancelled": the native uploader's record of the cancel (Android's
    // notification), kept until this card has been seen or closed.
    nativeRecord: null,
    ...fields,
  });

  // ---- state for the UI -------------------------------------------------

  const getState = (slug) => {
    if (snapshots.has(slug)) return snapshots.get(slug);
    if (!idleSnapshots.has(slug)) {
      idleSnapshots.set(slug, Object.freeze({ ...BLANK_STATE, slug }));
    }
    return idleSnapshots.get(slug);
  };

  // What the UI is told, refreshed at most every speedRefreshMs. The time
  // left counts down with the clock and only takes a new estimate when it is
  // clearly different (25% and 10 s either way) on two refreshes in a row, so
  // a burst or a short stall never makes it jump back and forth.
  const refreshShownSpeed = (job, nowMs, sent) => {
    if (job.measuringSince == null || job.speedAverage == null) return;
    if (nowMs - job.measuringSince < settings.speedRefreshMs) return;
    if (job.shownAt != null && nowMs - job.shownAt < settings.speedRefreshMs) {
      return;
    }
    const estimate =
      job.etaRate > 0 ? Math.max(0, job.info.size - sent) / job.etaRate : null;
    job.etaMemory = steadyEta(job.etaMemory, estimate, nowMs);
    job.shownSpeed = job.speedAverage;
    job.shownEta = job.etaMemory?.shown ?? null;
    job.shownAt = nowMs;
  };

  // Exponentially weighted (time constant tauMs) bytes and time, so the
  // average needs no seed value and a spike counts for as long as it lasted.
  const decayInto = (total, bytes, spanMs, tauMs) => {
    const keep = Math.exp(-spanMs / tauMs);
    return { bytes: total.bytes * keep + bytes, ms: total.ms * keep + spanMs };
  };

  const speedStale = (job, nowMs = now()) =>
    job.lastMovedAt != null && nowMs - job.lastMovedAt >= settings.speedStaleMs;

  // Bytes stopped moving (a stall, or a network the platform hasn't reported
  // lost yet): once speedStaleMs has passed, publish so the card drops the
  // speed and time left.
  const watchStall = (job) => {
    if (job.stallTimer || job.lastMovedAt == null) return;
    const due = Math.max(0, job.lastMovedAt + settings.speedStaleMs - now());
    job.stallTimer = setTimer(() => {
      job.stallTimer = null;
      if (jobs.get(job.slug) !== job || job.lastMovedAt == null) return;
      if (speedStale(job)) publish(job.slug);
      else watchStall(job);
    }, due + 1);
  };

  const clearStallTimer = (job) => {
    if (job.stallTimer) clearTimer(job.stallTimer);
    job.stallTimer = null;
  };

  // Count bytes that went over the wire (a part sent again counts again) and
  // fold each half-second into the averages.
  const trackSpeed = (job, sent) => {
    const nowMs = now();
    // Moving again after a stall: the old speed is stale, measure afresh.
    if (job.lastSent != null && sent > job.lastSent && speedStale(job, nowMs)) {
      resetSpeed(job);
    }
    if (job.lastSent == null) {
      job.lastSent = sent;
      job.speedSince = nowMs;
      job.lastMovedAt = nowMs;
      watchStall(job);
      return;
    }
    const added = Math.max(0, sent - job.lastSent);
    job.lastSent = sent;
    if (added > 0) {
      job.lastMovedAt = nowMs;
      watchStall(job);
    }
    if (job.measuringSince == null) {
      if (nowMs - job.speedSince < settings.speedWarmupMs) return;
      job.measuringSince = nowMs;
      job.sampleStartedAt = nowMs;
      job.sampleBytes = 0;
      return;
    }
    job.sampleBytes += added;
    const span = nowMs - job.sampleStartedAt;
    if (span >= settings.speedSampleMs) {
      // A new part first fills the socket buffers: cap such spikes at 3x.
      const bytes =
        job.speedAverage > 0
          ? Math.min(job.sampleBytes, (job.speedAverage * 3 * span) / 1000)
          : job.sampleBytes;
      job.speedTotal = decayInto(
        job.speedTotal,
        bytes,
        span,
        settings.speedTimeConstantMs
      );
      job.speedAverage = (job.speedTotal.bytes * 1000) / job.speedTotal.ms;
      job.rateSamples.push({ t: nowMs, rate: (job.sampleBytes * 1000) / span });
      while (job.rateSamples[0].t <= nowMs - settings.etaWindowMs) {
        job.rateSamples.shift();
      }
      job.etaRate = medianOf(job.rateSamples.map((sample) => sample.rate));
      job.sampleStartedAt = nowMs;
      job.sampleBytes = 0;
    }
    refreshShownSpeed(job, nowMs, sent);
  };

  const resetSpeed = (job) => {
    job.lastSent = null;
    job.speedSince = null;
    job.measuringSince = null;
    job.sampleStartedAt = null;
    job.sampleBytes = 0;
    job.speedAverage = null;
    job.speedTotal = { bytes: 0, ms: 0 };
    job.rateSamples = [];
    job.etaRate = null;
    job.shownSpeed = null;
    job.shownEta = null;
    job.shownAt = null;
    job.etaMemory = null;
    job.lastMovedAt = null;
    clearStallTimer(job);
  };

  const clearResumeNotice = (job) => {
    if (job.resumeNoticeTimer) clearTimer(job.resumeNoticeTimer);
    job.resumeNoticeTimer = null;
    job.resumeNotice = null;
  };

  // "Resuming — X of Y MB were already uploaded" (or "Starting over" when
  // nothing had finished), for a few seconds after a session is resumed.
  const showResumeNotice = (job) => {
    clearResumeNotice(job);
    job.resumeNotice = Object.freeze({
      uploadedBytes: job.ackedBytes,
      size: job.info.size,
    });
    job.resumeNoticeTimer = setTimer(() => {
      job.resumeNoticeTimer = null;
      job.resumeNotice = null;
      if (jobs.get(job.slug) === job) publish(job.slug);
    }, settings.resumeNoticeMs);
  };

  // Processing has shown no progress for a while ("Still processing…").
  const stalledOf = (job) =>
    WATCHED.has(job.status) &&
    job.conversionStage === "processing" &&
    job.progressChangedAt != null &&
    now() - job.progressChangedAt > settings.progressStallMs;

  const queuedLongOf = (job) =>
    WATCHED.has(job.status) &&
    job.conversionStage === "queued" &&
    job.stageSince != null &&
    now() - job.stageSince >= settings.queuedLongMs;

  const conversionEtaOf = (job) =>
    WATCHED.has(job.status) &&
    job.conversionStage === "processing" &&
    job.conversionEta?.shown != null &&
    !stalledOf(job)
      ? Math.max(
          0,
          Math.ceil(
            job.conversionEta.shown - (now() - job.conversionEta.at) / 1000
          )
        )
      : null;

  const snapshotOf = (job) => {
    const size = job.info?.size || 0;
    const acked = Math.min(size, job.ackedBytes);
    const sent = Math.min(size, job.ackedBytes + job.inflightBytes);
    // Speed and time left only while parts are being sent (and bytes move).
    const sending =
      job.status === "uploading" && !job.detail && !speedStale(job);
    const speed = sending ? job.shownSpeed : null;
    return Object.freeze({
      slug: job.slug,
      status: job.status,
      detail: job.detail,
      id: job.session?.id || job.resumable?.id || null,
      filename: job.info?.name || null,
      size,
      mode: job.mode,
      insertIndex: job.insertIndex,
      notifyGuests: job.notifyGuests,
      meta: job.meta,
      bytesUploaded: acked,
      bytesSent: sent,
      percent: size ? Math.min(100, Math.floor((sent / size) * 100)) : 0,
      conversionProgress: job.conversionProgress,
      error: job.error,
      errorStage: job.errorStage,
      canRetry: job.canRetry,
      doneVia: job.doneVia,
      resumable: job.resumable,
      hasFile: Boolean(job.file),
      duplicate: job.duplicate,
      retrying: job.retrying,
      retryError: job.retryError,
      resumeNotice: job.resumeNotice,
      conversionStage: job.conversionStage,
      conversionNote: job.conversionNote,
      conversionEtaSeconds: conversionEtaOf(job),
      conversionStalled: stalledOf(job),
      queuedLong: queuedLongOf(job),
      retryable: job.retryable,
      speed: speed == null ? null : Math.round(speed),
      etaSeconds:
        speed > 0 && job.shownEta != null ? Math.ceil(job.shownEta) : null,
    });
  };

  const publish = (slug) => {
    const job = jobs.get(slug);
    if (job) {
      job.lastPublishAt = now();
      snapshots.set(slug, snapshotOf(job));
    } else {
      snapshots.delete(slug);
    }
    const sending = [...jobs.values()].some((item) => SENDING.has(item.status));
    if (sending !== leaveGuard) {
      leaveGuard = sending;
      try {
        adapter?.setLeaveGuard?.(sending);
      } catch (error) {
        // not essential
      }
    }
    const snapshot = getState(slug);
    const set = listeners.get(slug);
    if (set) {
      [...set].forEach((listener) => {
        try {
          listener(snapshot);
        } catch (error) {
          log(slug, "listener failed", error?.message);
        }
      });
    }
  };

  const setStatus = (job, status, detail = null) => {
    if (job.status !== status) log(job.slug, status, job.session?.id || "");
    job.status = status;
    job.detail = detail;
    publish(job.slug);
  };

  const subscribe = (slug, listener) => {
    let set = listeners.get(slug);
    if (!set) {
      set = new Set();
      listeners.set(slug, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (!set.size && listeners.get(slug) === set) listeners.delete(slug);
    };
  };

  const outcome = (job) => ({
    status: job.status,
    error: job.error,
    canRetry: job.canRetry,
    id: job.session?.id || null,
  });

  // ---- remembered sessions ---------------------------------------------

  const saveEntry = (job) => {
    if (!store || !job.fingerprint || !job.session?.uploadToken) return;
    store.save({
      fingerprint: job.fingerprint,
      id: job.session.id,
      uploadToken: job.session.uploadToken,
      partSize: job.session.partSize,
      partCount: job.session.partCount,
      slug: job.slug,
      filename: job.info.name,
      size: job.info.size,
      lastModified: job.info.lastModified ?? null,
      createdAt: job.session.createdAt || now(),
      expiresAt: job.session.expiresAt ?? null,
      mode: job.mode,
      insertIndex: job.insertIndex,
      notifyGuests: job.notifyGuests,
      meta: job.meta ?? null,
      status: "uploading",
    });
  };

  const forget = (job) => {
    if (job?.fingerprint) store?.remove(job.fingerprint);
  };

  const abortQuietly = (id, uploadToken) =>
    id && uploadToken
      ? Promise.resolve()
          .then(() => api.abort(id, uploadToken))
          .catch(() => {})
      : Promise.resolve();

  // ---- native uploader hooks (adapter; none on web) -----------------------

  // A picker copy nothing needs any more (native: deleted from Caches).
  const releaseFile = (file) => {
    if (!file) return;
    try {
      adapter?.releaseFile?.(file);
    } catch (error) {
      // not essential
    }
  };

  // A session the manager gives up: the native uploader drops its part files
  // and its copy of the video, and with `abort` evt.live aborts the session
  // (the native module does that itself when it has the upload).
  const discardSession = (id, uploadToken, { abort = true } = {}) => {
    if (!id) return Promise.resolve();
    if (!adapter?.discard) {
      return abort ? abortQuietly(id, uploadToken) : Promise.resolve();
    }
    return Promise.resolve()
      .then(() => adapter.discard(id, { abort }))
      .catch(() => false)
      .then((handled) =>
        abort && !handled ? abortQuietly(id, uploadToken) : undefined
      );
  };

  // ---- opening a session --------------------------------------------------

  const sessionFrom = (source, fallback = {}) => ({
    id: source.id || fallback.id,
    uploadToken: source.uploadToken || fallback.uploadToken || null,
    partSize: Number(source.partSize ?? fallback.partSize),
    partCount: Number(source.partCount ?? fallback.partCount),
    createdAt: toTime(source.createdAt) ?? fallback.createdAt ?? now(),
    expiresAt: parseExpiresAt(source.expiresAt, fallback.expiresAt ?? null),
  });

  const sameParams = (entry, job) =>
    normalizeMode(entry.mode) === job.mode &&
    indexOrNull(entry.insertIndex) === job.insertIndex &&
    Boolean(entry.notifyGuests) === job.notifyGuests;

  const createBody = (job) => ({
    slug: job.slug,
    filename: job.info.name,
    size: job.info.size,
    contentType: videoContentType(job.info),
    ...(job.info.lastModified != null
      ? { lastModified: job.info.lastModified }
      : {}),
    mode: job.mode,
    insertIndex: job.insertIndex,
    notifyGuests: job.notifyGuests,
    client: clientInfo(),
    ...(job.force ? { force: true } : {}),
  });

  const isDuplicate = (error) =>
    error?.status === 409 &&
    (error.code === "duplicate" || Boolean(error.existing));

  // The resumed session still has another placement (an older API kept it).
  const placementDiffers = (remote, job) => {
    const mode = normalizeStatus(remote?.mode);
    if (mode && mode !== job.mode) return true;
    return (
      job.mode === "insert" &&
      remote?.insertIndex !== undefined &&
      indexOrNull(remote.insertIndex) !== job.insertIndex
    );
  };

  // POST create. evt.live resumes an unfinished session for the same file
  // (fresh token, the parts B2 already holds, and this request's mode,
  // insertIndex and notifyGuests), or answers 409 duplicate when the file was
  // handed over in the last 24 h.
  async function createSession(job) {
    const body = createBody(job);
    const attempt = async () => {
      try {
        return { created: await api.create(body) };
      } catch (error) {
        if (isDuplicate(error)) return { duplicate: error.existing || {} };
        throw error;
      }
    };
    let result = await attempt();
    if (result.duplicate) return result;
    let created = result.created;
    if (created?.resumed) {
      let remote = null;
      try {
        remote = await api.status(created.id, created.uploadToken);
      } catch (error) {
        remote = null;
      }
      if (remote && placementDiffers(remote, job)) {
        // Safety net: never let a resumed session finish with the old mode.
        await discardSession(created.id, created.uploadToken);
        result = await attempt();
        if (result.duplicate) return result;
        created = result.created;
      } else if (Array.isArray(remote?.uploadedParts)) {
        created = { ...created, uploadedParts: remote.uploadedParts };
      }
    }
    if (!created?.id || !created?.uploadToken) {
      throw new UploadV2Error(
        t("Video upload is temporarily unavailable. Please try again later."),
        { code: "bad_response", retryable: true, stage: "create" }
      );
    }
    return {
      session: sessionFrom(created),
      uploadedParts: created.uploadedParts || [],
      resumed: Boolean(created.resumed),
    };
  }

  // Continue the session remembered for this file, or go through create.
  // Returns { session, uploadedParts }, { session, finished } or
  // { duplicate }.
  async function openSession(job, { useStored = true } = {}) {
    const entry = useStored ? store?.get(job.fingerprint) : null;
    if (
      entry &&
      entry.slug === job.slug &&
      entry.filename === job.info.name &&
      entry.size === job.info.size &&
      sameParams(entry, job)
    ) {
      let remote = null;
      try {
        remote = await api.status(entry.id, entry.uploadToken);
      } catch (error) {
        // An expired token is fine: create resumes with a fresh one.
        if (!GONE.has(error?.status)) throw error;
      }
      const state = normalizeStatus(remote?.status);
      if (state === "uploading") {
        return {
          session: sessionFrom(remote, entry),
          uploadedParts: remote.uploadedParts || [],
          resumed: true,
        };
      }
      if (state === "queued" || state === "converting") {
        return { session: sessionFrom({}, entry), finished: state };
      }
    }
    // Nothing to continue here, or the same file placed somewhere else:
    // create's resume moves the same B2 upload to the new placement.
    forget(job);
    return createSession(job);
  }

  // ---- running an upload --------------------------------------------------

  const startWatching = (job, { immediate = true } = {}) => {
    if (!WATCHED.has(job.status) || !job.session?.id) return;
    if (!(retainCounts.get(job.slug) > 0)) return;
    if (!job.watcher) {
      job.watcher = createStatusWatcher({
        intervalMs: () =>
          job.watchSince != null &&
          now() - job.watchSince >= settings.slowPollAfterMs
            ? settings.slowPollIntervalMs
            : settings.pollIntervalMs,
        setTimer,
        clearTimer,
        fetchStatus: () => api.status(job.session.id, job.session.uploadToken),
        onStatus: (remote) => applyRemoteStatus(job, remote),
        onError: (error) => {
          if (jobs.get(job.slug) !== job) return false;
          // Network trouble: keep polling. Lost access: rely on eventUpdated.
          return !GONE.has(error?.status);
        },
      });
    }
    job.watcher.start({ immediate });
  };

  const stopWatching = (job) => {
    job?.watcher?.stop();
  };

  // evt.live's errorMessage is written for people; error is technical.
  const userText = (value) =>
    typeof value === "string" &&
    value.trim() &&
    value.length <= 300 &&
    !/[<>{}]/.test(value)
      ? value.trim()
      : null;

  // Where the conversion is (stage, progress, a friendly note) and the
  // processing time left from the median progress rate. evt.live never lowers
  // the progress within a run; a new run (the worker retried, or the job was
  // delivered again) starts over, and so does the bar.
  const noteConversion = (job, state, remote) => {
    const nowMs = now();
    const stage = conversionStageOf(state, normalizeStatus(remote?.stage));
    const progress = progressOf(remote?.progress);
    const restarted =
      (STAGE_RANK[stage] ?? 0) < (STAGE_RANK[job.conversionStage] ?? 0) ||
      (progress != null &&
        job.conversionProgress != null &&
        progress < job.conversionProgress);
    if (restarted) job.conversionProgress = progress;
    if (restarted || stage !== job.conversionStage) {
      job.conversionStage = stage;
      job.stageSince = nowMs;
      job.progressSamples = [];
      job.progressChangedAt = nowMs;
      job.conversionEta = null;
    }
    const rising =
      progress != null &&
      (job.conversionProgress == null || progress > job.conversionProgress);
    if (rising) {
      job.conversionProgress = progress;
      job.progressChangedAt = nowMs;
    }
    // A new stage's first reading seeds the rate samples even if it's equal.
    if (rising || (progress != null && !job.progressSamples.length)) {
      job.progressSamples.push({
        t: toTime(remote?.progressUpdatedAt) ?? nowMs,
        progress: job.conversionProgress,
      });
      const newest = job.progressSamples[job.progressSamples.length - 1].t;
      while (
        job.progressSamples.length > 2 &&
        job.progressSamples[0].t < newest - settings.progressWindowMs
      ) {
        job.progressSamples.shift();
      }
    }
    job.conversionNote = userText(remote?.errorMessage);
    // A new estimate only with new progress; in between it counts down (and a
    // long stall hides it).
    if (!rising || stage !== "processing" || job.progressSamples.length < 2) {
      return;
    }
    const rates = [];
    for (let index = 1; index < job.progressSamples.length; index += 1) {
      const previous = job.progressSamples[index - 1];
      const sample = job.progressSamples[index];
      const seconds = (sample.t - previous.t) / 1000;
      if (seconds > 0)
        rates.push((sample.progress - previous.progress) / seconds);
    }
    const rate = rates.length ? medianOf(rates) : 0;
    if (rate > 0) {
      job.conversionEta = steadyEta(
        job.conversionEta,
        (100 - job.conversionProgress) / rate,
        nowMs
      );
    }
  };

  // Back in evt.live's hands (queued again or adopted): fresh bookkeeping.
  const startConversion = (job, stage, since = now()) => {
    job.conversionStage = stage;
    job.conversionProgress = null;
    job.stageSince = since;
    job.watchSince = now();
    job.progressSamples = [];
    job.progressChangedAt = now();
    job.conversionEta = null;
    job.conversionNote = null;
  };

  const conversionFailed = (job, remote) => {
    stopWatching(job);
    job.conversionStage = "failed";
    job.retryable = remote?.retryable !== false;
    job.error = userText(remote?.errorMessage) || CONVERSION_FAILED_MESSAGE;
    job.serverError =
      typeof remote?.error === "string" ? remote.error.slice(0, 200) : null;
    job.errorStage = "conversion";
    job.canRetry = false;
    forget(job);
    setStatus(job, "failed");
  };

  const markDone = (job, via) => {
    stopWatching(job);
    job.doneVia = via;
    job.conversionStage = "done";
    job.conversionProgress = 100;
    job.conversionEta = null;
    forget(job);
    setStatus(job, "done");
  };

  function applyRemoteStatus(job, remote) {
    if (jobs.get(job.slug) !== job || !WATCHED.has(job.status)) return "stop";
    const state = normalizeStatus(remote?.status);
    if (state === "queued" || state === "converting") {
      noteConversion(job, state, remote);
      setStatus(job, state);
      return undefined;
    }
    if (state === "done") {
      markDone(job, "status");
      return "stop";
    }
    if (state === "failed" || state === "aborted") {
      conversionFailed(job, remote);
      return "stop";
    }
    return undefined;
  }

  // The newest event seen is the baseline for spotting the coming conversion
  // (the event's video can't change before it ends).
  const refreshBaseline = (job) => {
    const latest = lastEvents.get(job.slug);
    if (isLoadedEvent(latest)) {
      job.baseline = {
        uploadedAt: laterUploadedAt(
          job.baseline?.uploadedAt,
          latest.datetime_uploaded
        ),
      };
    }
  };

  // The upload is in evt.live's hands: queued (normally), or already
  // converting/done when an earlier attempt or the server finished it.
  function serverFinished(job, serverStatus) {
    job.run = null;
    releaseFile(job.file);
    job.file = null;
    job.inflightBytes = 0;
    clearResumeNotice(job);
    resetSpeed(job);
    job.ackedBytes = job.info.size;
    job.error = null;
    job.errorStage = null;
    job.canRetry = false;
    refreshBaseline(job);
    if (serverStatus === "done") {
      markDone(job, "status");
      return;
    }
    const status = serverStatus === "converting" ? "converting" : "queued";
    if (job.fingerprint) store?.update(job.fingerprint, { status });
    startConversion(job, status === "converting" ? "processing" : "queued");
    setStatus(job, status);
    startWatching(job);
  }

  async function uploadFailed(job, error) {
    const stillOurs = () => jobs.get(job.slug) === job && !job.run;
    if (error?.code === "cancelled") {
      // Cancelled outside the app's run (Android: the notification's Cancel);
      // the native uploader has already aborted it on evt.live.
      cancelledElsewhere(job, "notification");
      return;
    }
    if (
      job.session &&
      (error?.code === "session_state" || error?.status === 409)
    ) {
      // The session isn't uploading any more: evt.live may have completed it
      // (its cron auto-completes uploads whose app went quiet).
      try {
        const remote = await api.status(
          job.session.id,
          job.session.uploadToken
        );
        if (!stillOurs()) return;
        const state = normalizeStatus(remote?.status);
        if (FINISHED_ON_SERVER.has(state)) {
          serverFinished(job, state);
          return;
        }
      } catch (statusError) {
        if (!stillOurs()) return;
      }
    }
    const retryable =
      Boolean(job.file) &&
      (error?.code === "exhausted" || error?.retryable === true);
    if (!retryable) {
      forget(job);
      // The same file has to be chosen again: drop what the platform kept.
      if (job.session?.id) {
        discardSession(job.session.id, job.session.uploadToken, {
          abort: false,
        });
      }
      releaseFile(job.file);
      job.session = null;
      job.parts = null;
      job.done = new Set();
      job.ackedBytes = 0;
    }
    job.inflightBytes = 0;
    clearResumeNotice(job);
    resetSpeed(job);
    job.error = error?.message || UPLOAD_INTERRUPTED_MESSAGE;
    job.errorStage = "upload";
    job.canRetry = retryable;
    log(job.slug, "upload failed", error?.code || "", error?.status || "");
    setStatus(job, "failed");
  }

  // evt.live already has this file from the last 24 h.
  function onDuplicate(job, existing) {
    const state = normalizeStatus(existing?.status);
    job.run = null;
    job.inflightBytes = 0;
    if ((state === "queued" || state === "converting") && existing?.id) {
      // Still on its way: follow that upload instead of sending it again.
      forget(job);
      job.session = { id: existing.id, uploadToken: null };
      if (existing.mode) job.mode = normalizeMode(existing.mode);
      serverFinished(job, state);
      return;
    }
    job.duplicate = Object.freeze({
      id: existing?.id || null,
      status: state || "done",
      createdAt: existing?.createdAt || null,
      mode: existing?.mode || null,
    });
    setStatus(job, "duplicate");
  }

  // Use what openSession returned; false when there is nothing to upload.
  // A resumed session counts its parts from the start, and (unless only the
  // token was refreshed) says so for a few seconds.
  const proceedWith = (job, opened, { announce = true } = {}) => {
    if (opened.duplicate) {
      onDuplicate(job, opened.duplicate);
      return false;
    }
    job.session = opened.session;
    if (opened.finished) {
      serverFinished(job, opened.finished);
      return false;
    }
    job.parts = planParts(
      job.info.size,
      job.session.partSize,
      job.session.partCount
    );
    job.done = uploadedPartNumbers(opened.uploadedParts, job.parts);
    job.ackedBytes = job.parts.reduce(
      (sum, part) => (job.done.has(part.partNumber) ? sum + part.size : sum),
      0
    );
    if (opened.resumed && announce) showResumeNotice(job);
    saveEntry(job);
    return true;
  };

  async function runJob(job) {
    const run = { controller: new AbortController() };
    job.run = run;
    const { signal } = run.controller;
    const current = () => job.run === run && jobs.get(job.slug) === job;
    try {
      if (!job.session) {
        const opened = await openSession(job);
        if (!current()) {
          // Cancelled while the session was being created: don't leave it
          // counting against the account's active uploads.
          if (job.cancelled && opened.session && !opened.finished) {
            abortQuietly(opened.session.id, opened.session.uploadToken);
          }
          return outcome(job);
        }
        if (!proceedWith(job, opened)) return outcome(job);
      }

      let tokenRefreshed = false;
      for (;;) {
        if (signal.aborted) throw abortError(signal);
        try {
          const options = uploadOptions(job, run, signal);
          // Native platforms send the parts and complete in their own
          // uploader (background URLSession on iOS).
          const result = adapter?.runUpload
            ? await adapter.runUpload(options)
            : await runUpload(options);
          if (!current()) return outcome(job);
          serverFinished(job, result.status);
          return outcome(job);
        } catch (error) {
          if (error?.code !== "token" || tokenRefreshed || !current()) {
            throw error;
          }
          // The upload token expired (7 days) or was refused: creating the
          // session again resumes the same B2 upload with a fresh token.
          tokenRefreshed = true;
          log(job.slug, "new upload token", job.session?.id || "");
          const opened = await openSession(job, { useStored: false });
          if (!current()) return outcome(job);
          if (!proceedWith(job, opened, { announce: false })) {
            return outcome(job);
          }
        }
      }
    } catch (error) {
      if (!current()) return outcome(job);
      job.run = null;
      if (isAbortError(error)) {
        job.inflightBytes = 0;
        clearResumeNotice(job);
        resetSpeed(job);
        setStatus(job, "paused");
        return outcome(job);
      }
      await uploadFailed(job, error);
      return outcome(job);
    }
  }

  function uploadOptions(job, run, signal) {
    const current = () => job.run === run && jobs.get(job.slug) === job;
    return {
      file: job.file,
      parts: job.parts,
      done: job.done,
      session: job.session,
      api,
      adapter,
      signal,
      concurrency: settings.concurrency,
      initialConcurrency: settings.initialConcurrency,
      minConcurrency: settings.minConcurrency,
      maxConcurrency: settings.maxConcurrency,
      slowPartMs: settings.slowPartMs,
      signBatchSize: settings.signBatchSize,
      maxTries: settings.maxTries,
      backoffBaseMs: settings.backoffBaseMs,
      backoffMaxMs: settings.backoffMaxMs,
      urlExpiryMarginMs: settings.urlExpiryMarginMs,
      completeRounds: settings.completeRounds,
      random,
      now,
      sleep: wait,
      // What a native uploader saves with the job, to follow it again after
      // a relaunch (the web engine ignores it).
      upload: {
        slug: job.slug,
        fingerprint: job.fingerprint,
        mode: job.mode,
        insertIndex: job.insertIndex,
        notifyGuests: job.notifyGuests,
        meta: job.meta,
        info: job.info,
      },
      onPhase: (phase) => {
        if (!current()) return;
        job.inflightBytes = 0;
        resetSpeed(job);
        setStatus(job, phase === "completing" ? "completing" : "uploading");
      },
      onProgress: ({ ackedBytes, inflightBytes }) => {
        if (!current()) return;
        const partDone = ackedBytes !== job.ackedBytes;
        job.ackedBytes = ackedBytes;
        job.inflightBytes = inflightBytes;
        trackSpeed(job, ackedBytes + inflightBytes);
        // Finished parts always show; bytes in flight at most every 200 ms.
        if (
          partDone ||
          now() - job.lastPublishAt >= settings.progressIntervalMs
        ) {
          publish(job.slug);
        }
      },
      onNetworkWait: (offline) => {
        if (!current()) return;
        const detail = offline ? "offline" : null;
        if (offline) resetSpeed(job);
        if (job.detail !== detail) setStatus(job, job.status, detail);
      },
      onConcurrency: (limit) => {
        if (current()) log(job.slug, "parts in flight", limit);
      },
    };
  }

  const teardown = (job) => {
    if (!job) return;
    stopWatching(job);
    clearResumeNotice(job);
    clearStallTimer(job);
    if (job.run) {
      const { controller } = job.run;
      job.run = null;
      controller.abort("replaced");
    }
  };

  const failLocally = (job, message) => {
    job.detail = null;
    job.error = message;
    job.errorStage = "upload";
    job.canRetry = false;
    job.status = "failed";
    publish(job.slug);
    return Promise.resolve(outcome(job));
  };

  // ---- public actions -----------------------------------------------------

  function start(slug, options = {}) {
    // Native: follow the uploads still running from before first, so the
    // same event can't get a second one.
    if (adoption && !adoptionDone && slug && options?.file) {
      return adoption.then(() => startNow(slug, options));
    }
    return startNow(slug, options);
  }

  function startNow(
    slug,
    {
      file,
      mode = null,
      insertIndex = null,
      notifyGuests = false,
      meta = null,
      event = null,
      uploadedBytes = 0,
    } = {}
  ) {
    if (!slug || !file) {
      return Promise.resolve({
        status: "idle",
        error: t("Choose a video file."),
      });
    }
    const existing = jobs.get(slug);
    if (existing && ACTIVE.has(existing.status)) {
      return Promise.resolve({ ...outcome(existing), error: BUSY_MESSAGE });
    }
    teardown(existing);
    if (existing?.file && existing.file !== file) releaseFile(existing.file);
    if (existing?.status === "cancelled") forgetNativeRecord(existing);
    const info = describe(file);
    const fingerprint = fingerprintFor({
      slug,
      name: info.name,
      size: info.size,
      lastModified: info.lastModified,
    });
    if (
      existing?.status === "failed" &&
      existing.session &&
      existing.fingerprint !== fingerprint
    ) {
      // A different file instead of retrying: let the stalled session go.
      discardSession(existing.session.id, existing.session.uploadToken);
      forget(existing);
    }
    const job = newJob(slug, {
      file,
      info,
      fingerprint,
      mode: normalizeMode(mode),
      insertIndex: indexOrNull(insertIndex),
      notifyGuests: Boolean(notifyGuests),
      meta: meta ?? null,
      baseline: baselineFrom(
        isLoadedEvent(event) ? event : lastEvents.get(slug)
      ),
      status: "uploading",
      detail: "preparing",
      // A resume offer knows how much is there: the bar starts from it.
      ackedBytes: Math.min(info.size, Math.max(0, Number(uploadedBytes) || 0)),
    });
    jobs.set(slug, job);
    if (!(info.size >= 1)) return failLocally(job, EMPTY_FILE_MESSAGE);
    if (info.size > MAX_UPLOAD_BYTES)
      return failLocally(job, TOO_LARGE_MESSAGE);
    publish(slug);
    return runJob(job);
  }

  function pause(slug) {
    const job = jobs.get(slug);
    if (!job || job.status !== "uploading" || !job.run || !job.session) {
      return false;
    }
    const { controller } = job.run;
    job.run = null;
    controller.abort("paused");
    job.inflightBytes = 0;
    clearResumeNotice(job);
    resetSpeed(job);
    setStatus(job, "paused");
    return true;
  }

  // Continue a paused upload, or retry one that failed with canRetry. Pass the
  // file again when the manager no longer has it.
  function resume(slug, { file } = {}) {
    const job = jobs.get(slug);
    const resumable =
      job &&
      !job.run &&
      (job.status === "paused" || (job.status === "failed" && job.canRetry));
    if (!resumable) return Promise.resolve(null);
    if (file) {
      if (!sameFile(job.info, describe(file))) {
        return Promise.resolve({
          ...outcome(job),
          error: differentFileMessage(job.info.name),
        });
      }
      if (job.file && job.file !== file) releaseFile(job.file);
      job.file = file;
    }
    if (!job.file) {
      return Promise.resolve({ ...outcome(job), error: PICK_AGAIN_MESSAGE });
    }
    job.error = null;
    job.errorStage = null;
    job.canRetry = false;
    setStatus(job, "uploading", job.session ? null : "preparing");
    return runJob(job);
  }

  // "Upload cancelled" on the card until it's closed (dismiss) or another
  // video is chosen, also across visits to the screen; the event's video is
  // unchanged.
  const showCancelled = (job, { nativeRecord = null } = {}) => {
    jobs.set(
      job.slug,
      newJob(job.slug, {
        info: job.info,
        fingerprint: job.fingerprint,
        mode: job.mode,
        insertIndex: job.insertIndex,
        notifyGuests: job.notifyGuests,
        meta: job.meta,
        status: "cancelled",
        nativeRecord,
      })
    );
    publish(job.slug);
  };

  // The native uploader may let the cancel go once the card has been seen.
  const forgetNativeRecord = (job) => {
    if (!job?.nativeRecord) return;
    discardSession(job.nativeRecord, null, { abort: false });
    job.nativeRecord = null;
  };

  // Cancelled outside the app: Android's notification (live, or found later
  // in the native uploader's jobs) or, per evt.live, anywhere else. The card
  // says "Upload cancelled".
  function cancelledElsewhere(job, how) {
    const id = job.session?.id || "";
    log(job.slug, `cancelled (${how})`, id);
    forget(job);
    releaseFile(job.file);
    // Not at once: the native uploader keeps its record until the card has
    // been seen (markSeen) or closed, so a restart in between still shows it.
    showCancelled(job, { nativeRecord: id || null });
  }

  // evt.live aborted the session because it was cancelled (not the 7-day
  // expiry, error "expired").
  const abortedByOwner = (remote) =>
    normalizeStatus(remote?.status) === "aborted" &&
    normalizeStatus(remote?.error) !== "expired";

  // Stop and discard an upload that hasn't been handed to evt.live yet (also
  // discards a remembered session offered for resuming). An upload that had
  // started then shows "Upload cancelled" (Close); a duplicate or a resume
  // offer just goes away.
  function cancel(slug) {
    const job = jobs.get(slug);
    if (!job) return false;
    const started =
      job.status === "uploading" ||
      job.status === "paused" ||
      (job.status === "failed" && job.errorStage === "upload");
    const cancellable =
      started ||
      job.status === "duplicate" ||
      (job.status === "idle" && Boolean(job.resumable));
    if (!cancellable) return false;
    log(slug, "cancel", job.session?.id || job.resumable?.id || "");
    job.cancelled = true;
    teardown(job);
    if (job.session) discardSession(job.session.id, job.session.uploadToken);
    else if (job.resumable) discardSession(job.resumable.id, job.resumeToken);
    releaseFile(job.file);
    forget(job);
    if (started) {
      showCancelled(job);
    } else {
      jobs.delete(slug);
      publish(slug);
    }
    return true;
  }

  // Clear a finished or failed upload (and a resume offer, until next time).
  function dismiss(slug) {
    const job = jobs.get(slug);
    if (!job) return false;
    if (job.status === "failed" && job.errorStage === "upload" && job.session) {
      return cancel(slug);
    }
    if (ACTIVE.has(job.status) || job.run) return false;
    if (job.status === "failed" && job.errorStage === "conversion") {
      store?.markDismissed(job.session?.id);
    }
    if (job.status === "cancelled") forgetNativeRecord(job);
    teardown(job);
    releaseFile(job.file);
    jobs.delete(slug);
    publish(slug);
    return true;
  }

  // Manage Video shows the "Upload cancelled" card: the native uploader's
  // record can go (a later start of the app has nothing more to say). The
  // card itself stays until it's closed.
  function markSeen(slug) {
    const job = jobs.get(slug);
    if (!job || job.status !== "cancelled") return false;
    forgetNativeRecord(job);
    return true;
  }

  // "Upload again" after evt.live said it already has this file.
  function uploadAgain(slug) {
    const job = jobs.get(slug);
    if (!job || job.status !== "duplicate" || job.run || !job.file) {
      return Promise.resolve(null);
    }
    job.force = true;
    job.duplicate = null;
    setStatus(job, "uploading", "preparing");
    return runJob(job);
  }

  // "Try again" on a failed conversion: evt.live queues the uploaded file
  // again (no re-upload).
  async function retryConversion(slug) {
    const job = jobs.get(slug);
    if (
      !job ||
      job.status !== "failed" ||
      job.errorStage !== "conversion" ||
      !job.session?.id
    ) {
      return { error: RETRY_FAILED_MESSAGE };
    }
    if (job.retrying) return { pending: true };
    job.retrying = true;
    job.retryError = null;
    publish(slug);
    let result;
    try {
      result = await api.retry(job.session.id, job.session.uploadToken);
    } catch (error) {
      if (jobs.get(slug) !== job) return { error: null };
      job.retrying = false;
      job.retryError = error?.message || RETRY_FAILED_MESSAGE;
      publish(slug);
      return { error: job.retryError };
    }
    if (jobs.get(slug) !== job) return { error: null };
    const state = normalizeStatus(result?.status);
    job.retrying = false;
    job.error = null;
    job.serverError = null;
    job.errorStage = null;
    job.doneVia = null;
    job.conversionProgress = null;
    // The old watcher finished with the failure; the new run needs its own.
    stopWatching(job);
    job.watcher = null;
    if (state === "done") {
      markDone(job, "status");
      return { status: "done" };
    }
    refreshBaseline(job);
    startConversion(job, state === "converting" ? "processing" : "queued");
    setStatus(job, state === "converting" ? "converting" : "queued");
    startWatching(job);
    return { status: job.status };
  }

  // The user picked a file to continue the remembered session on offer.
  function resumeWithFile(slug, file, { event } = {}) {
    const job = jobs.get(slug);
    const offer = job && job.status === "idle" ? job.resumable : null;
    if (!offer || !file) return { error: NOTHING_TO_RESUME_MESSAGE };
    const expected = {
      name: offer.filename,
      size: offer.size,
      lastModified: offer.lastModified,
    };
    if (!sameFile(expected, describe(file))) {
      return { error: differentFileMessage(offer.filename) };
    }
    const promise = start(slug, {
      file,
      mode: offer.mode,
      insertIndex: offer.insertIndex,
      notifyGuests: offer.notifyGuests,
      meta: offer.meta,
      event,
      uploadedBytes: offer.uploadedBytes,
    });
    return { started: true, promise };
  }

  // ---- uploads a native uploader kept running --------------------------------

  // The native uploader (iOS) keeps uploading while the app is in the
  // background and keeps its jobs across relaunches. After a relaunch the
  // manager follows them again like uploads started here; the file is the
  // native module's own copy, so Retry/Resume don't need a new pick.
  let adoption = null;
  let adoptionDone = false;

  function adoptNative(entry) {
    const id = typeof entry?.uploadId === "string" ? entry.uploadId : null;
    const slug = typeof entry?.slug === "string" ? entry.slug : null;
    if (!id) return;
    if (!slug) {
      discardSession(id, null, { abort: false });
      return;
    }
    const current = jobs.get(slug);
    if (
      current &&
      (current.session?.id === id || current.run || ACTIVE.has(current.status))
    ) {
      // Already followed (or another upload for this event); a cancelled
      // record of an upload replaced since has nothing to tell.
      if (
        normalizeStatus(entry.state) === "cancelled" &&
        current.session?.id !== id
      ) {
        discardSession(id, null, { abort: false });
      }
      return;
    }
    const saved =
      entry.meta && typeof entry.meta === "object" ? entry.meta : {};
    const size = Number(entry.size) || 0;
    const info = {
      name: String(entry.filename || "video"),
      size,
      lastModified: Number.isFinite(saved.lastModified)
        ? saved.lastModified
        : null,
      type: typeof saved.type === "string" ? saved.type : "",
    };
    const session = {
      id,
      uploadToken: entry.uploadToken || null,
      partSize: Number(entry.partSize),
      partCount: Number(entry.partCount),
      createdAt: toTime(entry.createdAt) ?? now(),
      expiresAt: null,
    };
    let parts;
    try {
      parts = planParts(size, session.partSize, session.partCount);
    } catch (error) {
      discardSession(id, session.uploadToken, { abort: false });
      return;
    }
    const job = newJob(slug, {
      file: entry.fileUri
        ? {
            uri: entry.fileUri,
            name: info.name,
            size,
            lastModified: info.lastModified,
            type: info.type,
            owned: false,
          }
        : null,
      info,
      fingerprint:
        typeof saved.fingerprint === "string"
          ? saved.fingerprint
          : fingerprintFor({
              slug,
              name: info.name,
              size,
              lastModified: info.lastModified,
            }),
      mode: normalizeMode(saved.mode),
      insertIndex: indexOrNull(saved.insertIndex),
      notifyGuests: Boolean(saved.notifyGuests),
      meta: saved.meta ?? null,
      baseline: baselineFrom(lastEvents.get(slug)),
      session,
      parts,
      ackedBytes: Math.max(0, Math.min(size, Number(entry.bytesAcked) || 0)),
    });
    // A newer upload for this event replaces an "Upload cancelled" card: its
    // native record has nothing more to say.
    if (current?.status === "cancelled") forgetNativeRecord(current);
    teardown(current);
    jobs.set(slug, job);
    const state = normalizeStatus(entry.state);
    log(slug, "native upload", state, id);
    if (state === "uploading" || state === "completing") {
      job.status = state;
      // "Resuming — X of Y MB were already uploaded", as after a re-pick.
      if (state === "uploading") showResumeNotice(job);
      publish(slug);
      runJob(job);
    } else if (state === "paused") {
      job.status = "paused";
      publish(slug);
    } else if (state === "queued") {
      // Completed while the app wasn't watching.
      discardSession(id, null, { abort: false });
      serverFinished(job, normalizeStatus(entry.serverStatus) || "queued");
    } else if (state === "failed" && entry.error?.code === "token") {
      // runJob gets a fresh token (create resumes the same upload).
      job.status = "uploading";
      publish(slug);
      runJob(job);
    } else if (state === "failed") {
      job.status = "uploading";
      uploadFailed(
        job,
        entry.error ||
          new UploadV2Error(UPLOAD_INTERRUPTED_MESSAGE, {
            code: "exhausted",
            retryable: true,
          })
      );
    } else if (state === "cancelled") {
      // Cancelled from Android's notification while no JavaScript was
      // listening (no screen open, or the process was started just for the
      // notification): the card says so when its screen opens.
      cancelledElsewhere(job, "notification");
    } else {
      jobs.delete(slug);
      discardSession(id, null, { abort: false });
      publish(slug);
    }
  }

  async function adoptNativeJobs() {
    try {
      await adapter.prepare?.();
    } catch (error) {
      // the resume store just starts empty
    }
    if (typeof adapter.listJobs !== "function") return;
    let entries = [];
    try {
      entries = await adapter.listJobs();
    } catch (error) {
      log("-", "native uploads unavailable", error?.message);
      return;
    }
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      try {
        adoptNative(entry);
      } catch (error) {
        log(entry?.slug || "-", "native upload skipped", error?.message);
      }
    });
  }

  // ---- screens --------------------------------------------------------------

  const isIdle = (slug) => {
    const job = jobs.get(slug);
    return !job || (job.status === "idle" && !job.run);
  };

  function offerResume(slug, entry, remote) {
    const plan = (() => {
      try {
        return planParts(
          entry.size,
          remote?.partSize ?? entry.partSize,
          remote?.partCount ?? entry.partCount
        );
      } catch (error) {
        return null;
      }
    })();
    let uploadedBytes = Number(remote?.uploadedBytes);
    if (!Number.isFinite(uploadedBytes)) {
      uploadedBytes = 0;
      if (plan) {
        uploadedPartNumbers(remote?.uploadedParts, plan).forEach(
          (partNumber) => {
            uploadedBytes += plan[partNumber - 1].size;
          }
        );
      }
    }
    uploadedBytes = Math.max(0, Math.min(entry.size, uploadedBytes));
    const mode = normalizeMode(entry.mode);
    const insertIndex = indexOrNull(entry.insertIndex);
    const notifyGuests = Boolean(entry.notifyGuests);
    const meta = entry.meta ?? null;
    jobs.set(
      slug,
      newJob(slug, {
        info: {
          name: entry.filename,
          size: entry.size,
          lastModified: entry.lastModified ?? null,
          type: "",
        },
        fingerprint: entry.fingerprint,
        mode,
        insertIndex,
        notifyGuests,
        meta,
        ackedBytes: uploadedBytes,
        resumeToken: entry.uploadToken,
        resumable: Object.freeze({
          id: entry.id,
          filename: entry.filename,
          size: entry.size,
          lastModified: entry.lastModified ?? null,
          mode,
          insertIndex,
          notifyGuests,
          meta,
          createdAt: entry.createdAt,
          uploadedBytes,
          verified: Boolean(remote),
        }),
      })
    );
    publish(slug);
  }

  function adopt(slug, { status, entry = null, remote = {} }) {
    const size = entry?.size || Number(remote?.size) || 0;
    const job = newJob(slug, {
      info: {
        name: entry?.filename || remote?.filename || "video",
        size,
        lastModified: entry?.lastModified ?? null,
        type: "",
      },
      fingerprint: entry?.fingerprint || null,
      mode: normalizeMode(entry?.mode || remote?.mode),
      insertIndex: indexOrNull(entry?.insertIndex ?? remote?.insertIndex),
      notifyGuests: Boolean(entry?.notifyGuests),
      meta: entry?.meta ?? null,
      session: {
        id: entry?.id || remote?.id,
        uploadToken: entry?.uploadToken || null,
      },
      baseline: baselineFrom(lastEvents.get(slug)),
      status,
      ackedBytes: size,
    });
    jobs.set(slug, job);
    if (status === "failed") {
      conversionFailed(job, remote);
      return;
    }
    // Waiting since it was queued (when evt.live says), not since this page
    // loaded.
    startConversion(
      job,
      conversionStageOf(status, normalizeStatus(remote?.stage)),
      (status === "queued" &&
        (toTime(remote?.queuedAt) ?? toTime(remote?.updatedAt))) ||
        now()
    );
    noteConversion(job, status, remote);
    publish(slug);
    // The status was just fetched: next check after a full interval.
    startWatching(job, { immediate: false });
  }

  // What does this event have in flight? Remembered sessions first (they come
  // with upload tokens), then evt.live's list for this event.
  async function runDiscovery(slug) {
    if (adoption) await adoption;
    const entries = store ? store.listForSlug(slug) : [];
    for (const entry of entries) {
      if (!isIdle(slug)) return;
      let remote;
      try {
        remote = await api.status(entry.id, entry.uploadToken);
      } catch (error) {
        if (GONE.has(error?.status)) {
          store.remove(entry.fingerprint);
          continue;
        }
        // Can't check right now; resuming checks again.
        if (isIdle(slug)) offerResume(slug, entry, null);
        return;
      }
      if (!isIdle(slug)) return;
      const state = normalizeStatus(remote?.status);
      if (state === "uploading") {
        offerResume(slug, entry, remote);
        return;
      }
      if (state === "queued" || state === "converting") {
        adopt(slug, { status: state, entry, remote });
        return;
      }
      if (state === "failed" && !store.isDismissed(entry.id)) {
        adopt(slug, { status: "failed", entry, remote });
        return;
      }
      if (abortedByOwner(remote)) {
        // Cancelled where this screen couldn't see it (Android's
        // notification while the app wasn't listening, another tab).
        const job = newJob(slug, {
          info: {
            name: entry.filename,
            size: entry.size,
            lastModified: entry.lastModified ?? null,
            type: "",
          },
          fingerprint: entry.fingerprint,
          mode: normalizeMode(entry.mode),
          insertIndex: indexOrNull(entry.insertIndex),
          notifyGuests: Boolean(entry.notifyGuests),
          meta: entry.meta ?? null,
          session: { id: entry.id, uploadToken: entry.uploadToken },
        });
        jobs.set(slug, job);
        cancelledElsewhere(job, "evt.live");
        return;
      }
      store.remove(entry.fingerprint); // done, expired or dismissed
    }

    if (typeof api.list !== "function" || !isIdle(slug)) return;
    let sessions;
    try {
      sessions = await api.list(slug);
    } catch (error) {
      return;
    }
    if (!isIdle(slug) || !Array.isArray(sessions)) return;
    const latest = sessions.map(listedSession).find(Boolean);
    if (!latest) return;
    if (latest.status === "queued" || latest.status === "converting") {
      adopt(slug, { status: latest.status, remote: latest });
      return;
    }
    const updatedAt = toTime(latest.updatedAt);
    if (
      latest.status === "failed" &&
      !store?.isDismissed(latest.id) &&
      updatedAt != null &&
      now() - updatedAt <= settings.failureMaxAgeMs
    ) {
      adopt(slug, { status: "failed", remote: latest });
    }
  }

  function discover(slug) {
    if (discoveries.has(slug)) return discoveries.get(slug);
    const last = lastDiscoveryAt.get(slug);
    if (last != null && now() - last < settings.discoverIntervalMs) {
      return Promise.resolve();
    }
    lastDiscoveryAt.set(slug, now());
    const task = runDiscovery(slug)
      .catch((error) => log(slug, "discovery failed", error?.message))
      .finally(() => discoveries.delete(slug));
    discoveries.set(slug, task);
    return task;
  }

  const stoppedWithSession = (job) =>
    !job.run &&
    Boolean(job.session?.id) &&
    (job.status === "paused" ||
      (job.status === "failed" && job.errorStage === "upload"));

  // A stopped upload may have been cancelled, resumed or finished outside
  // the app (Android's notification) while no event could reach it: when its
  // screen opens, ask the native uploader (else evt.live) and follow.
  async function reconcileStopped(job) {
    const id = job.session.id;
    const still = () => jobs.get(job.slug) === job && stoppedWithSession(job);
    if (typeof adapter?.listJobs === "function") {
      let entries = null;
      try {
        entries = await adapter.listJobs();
      } catch (error) {
        entries = null;
      }
      if (!still()) return;
      const native = Array.isArray(entries)
        ? entries.find((item) => item?.uploadId === id)
        : null;
      if (native) {
        const state = normalizeStatus(native.state);
        if (state === "cancelled") {
          cancelledElsewhere(job, "notification");
        } else if (
          (state === "uploading" || state === "completing") &&
          job.status === "paused"
        ) {
          log(job.slug, "resumed (notification)", id);
          resume(job.slug);
        } else if (state === "queued") {
          discardSession(id, null, { abort: false });
          serverFinished(job, normalizeStatus(native.serverStatus) || "queued");
        }
        return;
      }
    }
    let remote;
    try {
      remote = await api.status(id, job.session.uploadToken);
    } catch (error) {
      return;
    }
    if (still() && abortedByOwner(remote)) cancelledElsewhere(job, "evt.live");
  }

  // A mounted screen: polls evt.live while a conversion is pending and looks
  // for remembered sessions. Returns the release function.
  function retain(slug) {
    if (!slug) return () => {};
    const count = (retainCounts.get(slug) || 0) + 1;
    retainCounts.set(slug, count);
    if (count === 1) {
      const job = jobs.get(slug);
      if (job && WATCHED.has(job.status)) startWatching(job);
      if (isIdle(slug)) discover(slug);
      if (job && stoppedWithSession(job)) {
        reconcileStopped(job).catch((error) =>
          log(slug, "reconcile failed", error?.message)
        );
      }
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const remaining = (retainCounts.get(slug) || 1) - 1;
      if (remaining > 0) {
        retainCounts.set(slug, remaining);
        return;
      }
      retainCounts.delete(slug);
      const job = jobs.get(slug);
      stopWatching(job);
      // "Your video is ready" only shows while its screen is open; "Upload
      // cancelled" stays until it's closed.
      if (job?.status === "done") {
        teardown(job);
        jobs.delete(slug);
        publish(slug);
      }
    };
  }

  // The screen's event (eventUpdated subscription or a refetch).
  function observeEvent(slug, event) {
    if (!slug || !isLoadedEvent(event)) return;
    lastEvents.set(slug, event);
    const job = jobs.get(slug);
    if (!job || !WATCHED.has(job.status)) return;
    if (job.baseline === undefined) {
      job.baseline = baselineFrom(event);
      return;
    }
    if (eventShowsNewConversion(event, job.baseline.uploadedAt)) {
      markDone(job, "event");
    }
  }

  if (adapter?.prepare || adapter?.listJobs) {
    adoption = adoptNativeJobs()
      .catch((error) => log("-", "native uploads failed", error?.message))
      .finally(() => {
        adoptionDone = true;
      });
  }

  // Android's upload notification has Pause, Resume and Cancel. Pause reaches
  // the run that follows the upload; Resume and Cancel act on a paused upload,
  // which no run follows, so the manager picks them up from the native state
  // events (reason "notification").
  function followNative(event) {
    if (event?.reason !== "notification") return;
    const id = event.uploadId;
    const job = [...jobs.values()].find((item) => item.session?.id === id);
    if (!job || job.run) return;
    const stopped =
      job.status === "paused" ||
      (job.status === "failed" && job.errorStage === "upload");
    if (!stopped) return;
    const state = normalizeStatus(event.state);
    if (state === "cancelled") {
      cancelledElsewhere(job, "notification");
    } else if (state === "uploading") {
      log(job.slug, "resumed (notification)", id);
      resume(job.slug);
    }
  }

  if (typeof adapter?.watch === "function") {
    try {
      adapter.watch((event) => {
        try {
          followNative(event);
        } catch (error) {
          log("-", "native event skipped", error?.message);
        }
      });
    } catch (error) {
      // no native events: the app's own buttons still work
    }
  }

  // Back in the foreground: an open screen's stopped upload checks again
  // what happened to it meanwhile (as when the screen opens).
  if (typeof adapter?.onForeground === "function") {
    try {
      adapter.onForeground(() => {
        jobs.forEach((job, slug) => {
          if (retainCounts.get(slug) > 0 && stoppedWithSession(job)) {
            reconcileStopped(job).catch((error) =>
              log(slug, "reconcile failed", error?.message)
            );
          }
        });
      });
    } catch (error) {
      // the screen still checks when it opens
    }
  }

  return {
    getState,
    subscribe,
    start,
    pause,
    resume,
    cancel,
    dismiss,
    markSeen,
    resumeWithFile,
    uploadAgain,
    retryConversion,
    retain,
    observeEvent,
    discover,
  };
}
